import { useState, useEffect, createRef, useRef, ReactElement, FC } from 'react'
import cn from 'classnames'
import dynamic from 'next/dynamic'

import { Captcha } from 'components/app/captcha'
import Input from 'components/app/input'
import IconCross from 'images/icon-cross.svg'
import onlyNumberChanger from 'utils/only-numbers-value'
import { constants } from 'config/constants'

import { useTranslation } from 'utils/use-translation'

import styles from './styles.module.scss'

const AnimatedIconValid = dynamic(() => import('components/ui/animated-icon-valid'), { ssr: false })

const TIMER_RESEND = 60

interface Props {
  label?: string | ReactElement
  isValid: boolean
  value: string
  onChange: (value: string) => void
  onResendCode?: (captcha: string) => void | Promise<void>
  disabled?: boolean
  isVisibleError?: boolean
  onEnter?: () => void
  onResendTick?: (leftSec: number) => void
  hideResendText?: boolean
  toRestartResendTimer?: boolean
  resendBtnClassName?: string
  codeInputClassName?: string
  inputOnResendClassName?: string
  maxLength?: number
  disableResend?: boolean
  disableAutoFocus?: boolean
  className?: string
  errorCode?: string
  labelClassName?: string
}

const InputCode: FC<Props> = ({
  label,
  isValid,
  value,
  onChange,
  onResendCode,
  disabled,
  isVisibleError = true,
  onEnter,
  onResendTick,
  hideResendText,
  toRestartResendTimer,
  resendBtnClassName,
  codeInputClassName,
  inputOnResendClassName,
  maxLength,
  disableResend,
  disableAutoFocus,
  className,
  errorCode,
  labelClassName,
}) => {
  const { t } = useTranslation()

  const isError = !isValid && isVisibleError

  const [countSeconds, setCountSeconds] = useState(TIMER_RESEND)
  const [errorMsg, setErrorMsg] = useState('')

  const inputRef = createRef<HTMLInputElement>()
  const resendTimerRef = useRef(null)

  const handleChange = (event) => {
    onlyNumberChanger(event.target.value, onChange)
    setErrorMsg('')
  }

  const handleResend = async (captcha: string): Promise<void> => {
    await onResendCode(captcha)
    try {
      inputRef.current.focus()
    } catch (ignore) {}
    setCountSeconds(TIMER_RESEND)
  }

  const restartResendTimer = () => {
    clearTimeout(resendTimerRef.current)
    if (disableResend) return

    setCountSeconds(TIMER_RESEND)
    onResendTick?.(TIMER_RESEND)
    resendTimerRef.current = setInterval(() => {
      setCountSeconds((count) => {
        const newCount = Math.max(0, count - 1)
        onResendTick?.(newCount)
        return newCount
      })
    }, 1000)
  }

  useEffect(() => {
    if (!disabled) {
      restartResendTimer()
    }
    return () => clearInterval(resendTimerRef.current)
  }, [])

  useEffect(() => {
    if (disabled) {
      clearTimeout(resendTimerRef.current)
    } else if (!disabled) {
      restartResendTimer()
    }
  }, [disabled])

  useEffect(() => {
    if (toRestartResendTimer) {
      restartResendTimer()
    }
  }, [toRestartResendTimer])

  useEffect(() => {
    if (errorCode) {
      switch (errorCode) {
        case 'INTERNAL_ERROR':
          setErrorMsg(t('insufficient_funds'))
          break
        case 'INVALID_REQUEST_PARAMS':
          setErrorMsg(t('code_is_invalid'))
          break
        default:
          setErrorMsg(errorCode)
          break
      }
    }
  }, [errorCode])

  return (
    <div
      className={cn(styles.inputCode, className, {
        [inputOnResendClassName]: !disabled && countSeconds <= 0 && (!hideResendText || isValid),
      })}
    >
      {label && <p className={cn(styles.input__label, labelClassName)}>{label}</p>}
      <div className={cn(styles.phoneInput, codeInputClassName)}>
        <Input
          onEnter={onEnter}
          maxLength={maxLength || constants.codeLength}
          autoFocus={!disableAutoFocus}
          type="text"
          onChange={handleChange}
          readOnly={isValid}
          disabled={disabled}
          placeholder={[...Array(constants.codeLength).keys()].reduce((acc, i) => `${acc}${(i + 1) % 10}`, '')}
          value={value}
          ref={inputRef}
        />
        {isError && errorCode === 'INVALID_REQUEST_PARAMS' && (
          <img src={IconCross} alt="invalid" height={14} width={14} />
        )}
        {isValid && <AnimatedIconValid />}
      </div>

      {errorMsg && <p className={styles.errorMsg}>{errorMsg}</p>}

      {!hideResendText && !isValid && (
        <>
          {!disabled && countSeconds <= 0 && (
            <Captcha onCaptchaSuccess={handleResend}>
              <div className={cn(styles.resendCodeBtn, resendBtnClassName)}>{t('resend')}</div>
            </Captcha>
          )}

          {!disabled && countSeconds > 0 && (
            <div className={styles.resendCode}>
              {t('resend_code_in')} {countSeconds} {t('sec')}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default InputCode
